import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ContentBlockEditor, { ContentBlock } from '@/components/admin/ContentBlockEditor';
import ImageUpload from '@/components/admin/ImageUpload';
import { ArrowLeft, Save, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

// Catégories prédéfinies pour le dropdown
const ARTICLE_CATEGORIES = [
  { value: 'Marketing B2B', label: 'Marketing B2B' },
  { value: 'Création de contenu', label: 'Création de contenu' },
  { value: 'Marketing vidéo', label: 'Marketing vidéo' },
  { value: 'Stratégie', label: 'Stratégie' },
  { value: 'Accompagnement', label: 'Accompagnement' },
] as const;

interface FAQItem {
  question: string;
  answer: string;
}

const articleSchema = z.object({
  title: z.string().min(1, 'Le titre est requis'),
  slug: z.string().min(1, 'Le slug est requis').regex(/^[a-z0-9-]+$/, 'Le slug ne doit contenir que des lettres minuscules, chiffres et tirets'),
  excerpt: z.string().optional(),
  meta_title: z.string().optional(),
  meta_description: z.string().optional(),
  category: z.string().optional(),
  author: z.string().optional(),
  published_date: z.string().optional(),
  image_url: z.string().optional(),
  featured_video: z.string().optional(),
  local_seo: z.string().optional(),
  read_time: z.string().optional(),
  is_published: z.boolean(),
});

type ArticleFormData = z.infer<typeof articleSchema>;

const AdminArticleEditor = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!slug;

  const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>([]);
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [relatedArticles, setRelatedArticles] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ArticleFormData>({
    resolver: zodResolver(articleSchema),
    defaultValues: {
      is_published: false,
      author: 'Agence 8020',
    },
  });

  const title = watch('title');

  // Auto-generate slug from title
  useEffect(() => {
    if (!isEditing && title) {
      const generatedSlug = title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setValue('slug', generatedSlug);
    }
  }, [title, isEditing, setValue]);

  // Load existing article
  const { data: article, isLoading } = useQuery({
    queryKey: ['admin-article', slug],
    queryFn: async () => {
      if (!slug) return null;

      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: isEditing,
  });

  // Load all articles for "related articles" selector
  const { data: allArticles } = useQuery({
    queryKey: ['all-article-slugs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('slug, title')
        .order('title');

      if (error) throw error;
      return data;
    },
  });

  // Populate form when article is loaded
  useEffect(() => {
    if (article) {
      setValue('title', article.title);
      setValue('slug', article.slug);
      setValue('excerpt', article.excerpt || '');
      setValue('meta_title', article.meta_title || '');
      setValue('meta_description', article.meta_description || '');
      setValue('category', article.category || '');
      setValue('author', article.author || 'Agence 8020');
      setValue('published_date', article.published_date || '');
      setValue('image_url', article.image_url || '');
      setValue('featured_video', article.featured_video || '');
      setValue('local_seo', article.local_seo || '');
      setValue('read_time', article.read_time || '');
      setValue('is_published', article.is_published || false);

      if (Array.isArray(article.content)) {
        setContentBlocks(article.content as unknown as ContentBlock[]);
      }
      if (Array.isArray(article.faqs)) {
        setFaqs(article.faqs as unknown as FAQItem[]);
      }
      if (Array.isArray(article.related_articles)) {
        setRelatedArticles(article.related_articles);
      }
    }
  }, [article, setValue]);

  const saveMutation = useMutation({
    mutationFn: async (data: ArticleFormData) => {
      const payload = {
        title: data.title,
        slug: data.slug,
        excerpt: data.excerpt || null,
        meta_title: data.meta_title || null,
        meta_description: data.meta_description || null,
        category: data.category || null,
        author: data.author || 'Agence 8020',
        published_date: data.published_date || null,
        image_url: data.image_url || null,
        featured_video: data.featured_video || null,
        local_seo: data.local_seo || null,
        read_time: data.read_time || null,
        is_published: data.is_published,
        content: contentBlocks as unknown as Json,
        faqs: faqs as unknown as Json,
        related_articles: relatedArticles.filter(s => s.trim() !== ''),
        updated_at: new Date().toISOString(),
      };

      if (isEditing && article) {
        const { error } = await supabase
          .from('articles')
          .update(payload)
          .eq('id', article.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('articles')
          .insert(payload);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-articles'] });
      queryClient.invalidateQueries({ queryKey: ['admin-article-stats'] });
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      toast.success(isEditing ? 'Article mis à jour' : 'Article créé');
      navigate('/admin/articles');
    },
    onError: (error: any) => {
      if (error.message?.includes('duplicate key')) {
        toast.error('Ce slug existe déjà. Choisissez un autre slug.');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    },
  });

  const addFaq = () => {
    setFaqs([...faqs, { question: '', answer: '' }]);
  };

  const updateFaq = (index: number, field: 'question' | 'answer', value: string) => {
    const newFaqs = [...faqs];
    newFaqs[index][field] = value;
    setFaqs(newFaqs);
  };

  const removeFaq = (index: number) => {
    setFaqs(faqs.filter((_, i) => i !== index));
  };

  if (isEditing && isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/articles')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {isEditing ? 'Modifier l\'article' : 'Nouvel article'}
            </h1>
            <p className="text-muted-foreground">
              {isEditing ? article?.title : 'Créez un nouvel article de blog'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="is_published"
              checked={watch('is_published')}
              onCheckedChange={(checked) => setValue('is_published', checked)}
            />
            <Label htmlFor="is_published">
              {watch('is_published') ? 'Publié' : 'Brouillon'}
            </Label>
          </div>
          <Button
            onClick={handleSubmit((data) => saveMutation.mutate(data))}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Sauvegarder
          </Button>
        </div>
      </div>

      <Tabs defaultValue="content" className="space-y-6">
        <TabsList>
          <TabsTrigger value="content">Contenu</TabsTrigger>
          <TabsTrigger value="meta">Métadonnées</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
          <TabsTrigger value="faqs">FAQs</TabsTrigger>
          <TabsTrigger value="related">Articles reliés</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informations principales</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="title">Titre *</Label>
                  <Input id="title" {...register('title')} placeholder="Titre de l'article" />
                  {errors.title && <p className="text-sm text-destructive">{errors.title.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug *</Label>
                  <Input id="slug" {...register('slug')} placeholder="url-de-larticle" />
                  {errors.slug && <p className="text-sm text-destructive">{errors.slug.message}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="excerpt">Extrait</Label>
                <Textarea
                  id="excerpt"
                  {...register('excerpt')}
                  placeholder="Court résumé de l'article..."
                  rows={3}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="category">Catégorie</Label>
                  <Select
                    value={watch('category') || ''}
                    onValueChange={(value) => setValue('category', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      {ARTICLE_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="author">Auteur</Label>
                  <Input id="author" {...register('author')} placeholder="Agence 8020" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="read_time">Temps de lecture</Label>
                  <Input id="read_time" {...register('read_time')} placeholder="8 min" />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <ImageUpload
                  value={watch('image_url') || ''}
                  onChange={(url) => setValue('image_url', url)}
                  label="Image de l'article"
                />
                <div className="space-y-2">
                  <Label htmlFor="featured_video">ID vidéo YouTube</Label>
                  <Input id="featured_video" {...register('featured_video')} placeholder="dQw4w9WgXcQ" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="published_date">Date de publication</Label>
                <Input id="published_date" type="date" {...register('published_date')} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Contenu de l'article</CardTitle>
            </CardHeader>
            <CardContent>
              <ContentBlockEditor blocks={contentBlocks} onChange={setContentBlocks} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="meta" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Métadonnées</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="meta_title">Meta Title</Label>
                <Input id="meta_title" {...register('meta_title')} placeholder="Titre SEO (max 60 caractères)" />
                <p className="text-sm text-muted-foreground">
                  {watch('meta_title')?.length || 0}/60 caractères
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="meta_description">Meta Description</Label>
                <Textarea
                  id="meta_description"
                  {...register('meta_description')}
                  placeholder="Description SEO (max 160 caractères)"
                  rows={3}
                />
                <p className="text-sm text-muted-foreground">
                  {watch('meta_description')?.length || 0}/160 caractères
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="seo" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>SEO Local</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="local_seo">Texte SEO local</Label>
                <Textarea
                  id="local_seo"
                  {...register('local_seo')}
                  placeholder="Contenu additionnel pour le référencement local..."
                  rows={6}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="faqs" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Questions fréquentes</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addFaq}>
                <Plus className="h-4 w-4 mr-1" />
                Ajouter une FAQ
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {faqs.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  Aucune FAQ. Ajoutez-en une avec le bouton ci-dessus.
                </p>
              ) : (
                faqs.map((faq, index) => (
                  <div key={index} className="space-y-3 p-4 border rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        <div className="space-y-2">
                          <Label>Question</Label>
                          <Input
                            value={faq.question}
                            onChange={(e) => updateFaq(index, 'question', e.target.value)}
                            placeholder="Question..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Réponse</Label>
                          <Textarea
                            value={faq.answer}
                            onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                            placeholder="Réponse..."
                            rows={3}
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive shrink-0"
                        onClick={() => removeFaq(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="related" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Articles reliés</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Sélectionnez les slugs des articles reliés (séparés par des virgules ou ajoutez-les un par un).
              </p>
              
              <div className="space-y-2">
                {relatedArticles.map((relSlug, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={relSlug}
                      onChange={(e) => {
                        const newRelated = [...relatedArticles];
                        newRelated[index] = e.target.value;
                        setRelatedArticles(newRelated);
                      }}
                      placeholder="slug-de-larticle"
                      list="article-slugs"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setRelatedArticles(relatedArticles.filter((_, i) => i !== index))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <datalist id="article-slugs">
                  {allArticles?.filter(a => a.slug !== slug).map(a => (
                    <option key={a.slug} value={a.slug}>{a.title}</option>
                  ))}
                </datalist>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRelatedArticles([...relatedArticles, ''])}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter un article relié
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminArticleEditor;
